"use client"
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ProductTag from './product-tag';
import { useContext } from "react";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { borderRadiusShorthand, useLayoutStyle } from "@/lib/providers/layout-style-provider";
import "./product-tag.css";

type ProductTagRendererProps = {
    tags: any;
    iconOnly?: boolean;
    iconSize?: number;
    singleRow?: boolean;
    /** Shrink tags below the sm breakpoint; used by 3-column mobile product cards. */
    compactMobile?: boolean;
};

// Matches the 0.7 ratio the tight 3-column mobile card layout applies elsewhere (e.g. 20px -> 14px).
const COMPACT_MOBILE_TAG_SCALE = 0.7;
// Complement of Tailwind's `sm` (min-width: 640px) breakpoint the cards use.
const COMPACT_MOBILE_MEDIA_QUERY = '(max-width: 639.98px)';

const useIsCompactMobile = (compactMobile?: boolean) => {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        if (!compactMobile) return;
        const mediaQuery = window.matchMedia(COMPACT_MOBILE_MEDIA_QUERY);
        const update = () => setIsMobile(mediaQuery.matches);
        update();
        mediaQuery.addEventListener('change', update);
        return () => mediaQuery.removeEventListener('change', update);
    }, [compactMobile]);
    return !!compactMobile && isMobile;
};

const ProductTagRenderer = ({ tags, iconOnly, iconSize, singleRow, compactMobile }: ProductTagRendererProps) => {
    const filteredTags = iconOnly
        ? tags.filter((tag: any) => tag.show && (tag.iconUrl || tag.iconName))
        : tags.filter((tag: any) => tag.show);

    // single-row (and icon-only): compute how many fit, then show +N for leftover
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRowRef = useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = useState(filteredTags.length);
    const [overflowCount, setOverflowCount] = useState(0);

    const enableOverflowLogic = !!iconOnly || !!singleRow;
    const isCompactMobile = useIsCompactMobile(compactMobile);
    const iconScale = isCompactMobile ? COMPACT_MOBILE_TAG_SCALE : 1;
    const colorStyle = useContext(ColorStyleContext);
    const layoutStyle = useLayoutStyle();

    const indicatorClassName = singleRow
        ? `product-indicator-display shrink-0 items-center justify-center text-sm sm:px-[12px] px-2 sm:py-2 py-1 whitespace-nowrap`
        : 'inline-flex shrink-0 items-center justify-center rounded-full text-xs px-2 py-1 bg-gray-100 text-gray-700 whitespace-nowrap';

    const measure = () => {
        const container = containerRef.current;
        if (!container) return;

        const items = Array.from(
            (measureRowRef.current || container).querySelectorAll<HTMLElement>('[data-measure-item="1"]')
        );
        if (!items.length) {
            setVisibleCount(0);
            setOverflowCount(0);
            return;
        }

        const style = getComputedStyle(container);
        const gap = parseFloat(style.gap || style.columnGap || "0") || 0;
        const maxWidth = container.clientWidth;

        const widths = items.map(el => el.offsetWidth);
        let used = 0;
        let count = 0;

        for (let i = 0; i < widths.length; i++) {
            const w = widths[i];
            const extra = count > 0 ? gap : 0;
            if (used + extra + w <= maxWidth) {
                used += extra + w;
                count++;
            } else {
                break;
            }
        }

        // Always show at least 1 tag if we have any
        if (widths.length > 0 && count === 0) {
            count = 1;
            used = widths[0];
        }

        let leftover = widths.length - count;
        if (leftover > 0) {
            const measureIndicatorWidth = (n: number) => {
                const mount = measureRowRef.current || container;
                const indicator = document.createElement('span');
                indicator.className = indicatorClassName;
                indicator.style.visibility = 'hidden';
                indicator.style.position = 'absolute';
                indicator.textContent = `+${n}`;
                mount.appendChild(indicator);
                const w = indicator.offsetWidth;
                mount.removeChild(indicator);
                return w;
            };

            // Ensure "+N" fits; measure using the actual leftover count (not worst-case)
            while (count > 1) {
                const currentLeftover = widths.length - count;
                const indicatorW = measureIndicatorWidth(currentLeftover);
                if ((used + (count > 0 ? gap : 0) + indicatorW) <= maxWidth) break;

                const lastW = widths[count - 1];
                used -= lastW + (count > 1 ? gap : 0);
                count--;
            }

            leftover = widths.length - count;
        }

        setVisibleCount(count);
        setOverflowCount(Math.max(leftover, 0));
    };

    // Recompute on mount, resize, tag changes, and compact-scale changes
    useLayoutEffect(() => {
        if (!enableOverflowLogic) return;
        measure();
        const ro = new ResizeObserver(() => measure());
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTags, enableOverflowLogic, iconScale]);

    if (filteredTags.length === 0) return null;

    // Default behavior (multi-line): keep original
    if (!enableOverflowLogic) {
        return (
            <div className="flex gap-2 items-center flex-wrap">
                {filteredTags.map((tag: any) => (
                    <ProductTag key={tag.id} tag={tag} iconOnly={iconOnly} iconSize={iconSize} iconScale={iconScale} />
                ))}
            </div>
        );
    }

    return (
        <div
            id="product-tag-renderer-ctn"
            ref={containerRef}
            className={
                singleRow
                    ? "relative flex w-full justify-start gap-2 items-center flex-nowrap overflow-hidden"
                    : "relative flex gap-2 items-center flex-nowrap overflow-hidden"
            }
        >
            {filteredTags.slice(0, visibleCount).map((tag: any, index: number) => (
                <span
                    id={`product-tag-renderer-item-${tag.id}`}
                    key={tag.id}
                    data-tag-item="1"
                    className={
                        // If we must show at least 1 tag and also show +N, allow the first tag to shrink/truncate
                        // so the +N indicator doesn't get clipped.
                        overflowCount > 0 && visibleCount === 1 && index === 0 && !iconOnly
                            ? "inline-flex min-w-0"
                            : "inline-flex shrink-0"
                    }
                >
                    <ProductTag tag={tag} iconOnly={iconOnly} iconSize={iconSize} iconScale={iconScale} />
                </span>
            ))}
            {overflowCount > 0 && (
                <span
                    id="product-tag-renderer-overflow-indicator"
                    aria-label={`+${overflowCount} more`}
                    className={`text-xs ${indicatorClassName} ${overflowCount > 0 && visibleCount === 1 ? "-ml-1" : ""}`}
                    style={{
                        borderRadius: singleRow ? borderRadiusShorthand(layoutStyle) : undefined,
                        backgroundColor: singleRow ? colorStyle?.buttonBgNormal || "#006ef2" : undefined,
                        color: singleRow ? colorStyle?.buttonTextNormal || "#ffffff" : undefined,
                    }}
                >
                    +{overflowCount}
                </span>
            )}
            {/* Hidden measurement row (intrinsic widths; doesn't affect layout) */}
            <div
                ref={measureRowRef}
                aria-hidden="true"
                className="absolute left-0 top-0 invisible pointer-events-none flex flex-nowrap gap-2"
            >
                {filteredTags.map((tag: any) => (
                    <span
                        key={`m-${tag.id}`}
                        data-measure-item="1"
                        className="inline-flex shrink-0"
                    >
                        <ProductTag tag={tag} iconOnly={iconOnly} iconSize={iconSize} iconScale={iconScale} />
                    </span>
                ))}
            </div>
        </div>
    );
}

export default ProductTagRenderer